import { TriageBucket } from "../sensorHealth.utility";

const PAD = 6;
const WIDTH = 320;

const STROKE: Record<TriageBucket, string> = {
  working: "#059669",
  "needs-look": "#d97706",
  "not-responding": "#94a3b8",
  unlinked: "#6b7280",
};

type SensorSparklineProps = {
  values: number[];
  /** Shared upper bound for the Y axis (kept in step with the gauge). */
  scaleMax: number;
  /** Detection threshold: draws a dashed line and shades the detecting band above it. */
  threshold?: number | null;
  bucket: TriageBucket;
  /** viewBox height; the SVG scales to its container width. */
  height?: number;
};

/**
 * A dependency-free trend chart of this session's readings, scaled to `scaleMax` so it lines up with
 * the detection gauge. When a threshold is present it shades the "detecting" band and marks the line.
 */
export const SensorSparkline = ({ values, scaleMax, threshold = null, bucket, height = 140 }: SensorSparklineProps) => {
  if (values.length < 2) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-400">Waiting for more readings…</div>
    );
  }

  const usable = height - PAD * 2;
  const stepX = WIDTH / (values.length - 1);
  const y = (value: number) => PAD + (1 - Math.max(0, Math.min(1, value / scaleMax))) * usable;

  const points = values.map((value, index) => `${(index * stepX).toFixed(1)},${y(value).toFixed(1)}`).join(" ");
  const last = values[values.length - 1];
  const thresholdY = threshold == null ? null : y(threshold);
  const stroke = STROKE[bucket];

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${height}`}
      preserveAspectRatio="none"
      className="h-full w-full"
      role="img"
      aria-label="Readings this session"
    >
      {thresholdY != null && (
        <>
          <rect x="0" y="0" width={WIDTH} height={thresholdY} fill="rgb(16 185 129 / 0.07)" />
          <line
            x1="0"
            y1={thresholdY}
            x2={WIDTH}
            y2={thresholdY}
            stroke="rgb(148 163 184)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        </>
      )}
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      <circle cx={WIDTH} cy={y(last)} r={3.5} fill={stroke} />
    </svg>
  );
};
